import React, { useState, useEffect, ChangeEvent } from "react";
import { Container, Row, Col, Form, Button, Spinner } from "react-bootstrap";
import { Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import AppConstants from "../../AppConstants";
import IconAdd from "../../images/icon_add.png";
import { useUser } from "../../UserContext";

interface FormValues {
  firstName: string;
  lastName: string;
}

const Profile: React.FC = () => {
  const { user, setUser } = useUser();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [originalValues, setOriginalValues] = useState<FormValues>({
    firstName: "",
    lastName: ""
  });

  const ProfileSchema = Yup.object().shape({
    firstName: Yup.string().min(2).max(50).required("First name is required"),
    lastName: Yup.string().min(2).max(50).required("Last name is required")
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.get(AppConstants.ADMIN_PROFILE_API, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (response.status === 200) {
          const userData = {
            firstName: response.data.firstName,
            lastName: response.data.lastName,
            email: response.data.email,
            profileImage:
              response.data.profileImage || "https://placehold.co/150"
          };

          setUser(userData);
          setOriginalValues({
            firstName: response.data.firstName,
            lastName: response.data.lastName
          });
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.error || "Failed to fetch profile", {
          position: "top-right"
        });
      }
    };

    if (!user) fetchProfile();
  }, [setUser, user]);

  // Update original values when user data changes
  useEffect(() => {
    if (user) {
      setOriginalValues({
        firstName: user.firstName,
        lastName: user.lastName
      });
    }
  }, [user]);

  const handleUpdate = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ) => {
    try {
      const token = localStorage.getItem("authToken");

      const formData = new FormData();
      formData.append("firstName", values.firstName);
      formData.append("lastName", values.lastName);

      if (selectedFile) {
        formData.append("profileImage", selectedFile);
      }

      const response = await axios.put(
        AppConstants.ADMIN_EDIT_PROFILE_API,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );

      if (response.status === 200) {
        const updatedUser = response.data.user;
        setUser((prev) =>
          prev
            ? {
              ...prev,
              firstName: updatedUser.firstName,
              lastName: updatedUser.lastName,
              profileImage: updatedUser.profileImage
            }
            : updatedUser
        );

        // Update original values after successful update
        setOriginalValues({
          firstName: updatedUser.firstName,
          lastName: updatedUser.lastName
        });

        // Clear selected file after successful update
        setSelectedFile(null);

        toast.success(
          response.data.message || "Profile updated successfully!",
          {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true
          }
        );
      }
    } catch (error: any) {
      console.error("Profile update error:", error);
      toast.error(error?.response?.data?.error || "Failed to update profile", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files[0]) {
      const reader = new FileReader();
      setSelectedFile(files[0]);

      reader.onloadend = () => {
        if (reader.result && typeof reader.result === "string" && user) {
          setUser({ ...user, profileImage: reader.result });
        }
      };
      reader.readAsDataURL(files[0]);
    }
  };

  // Check if form has changes
  const hasChanges = (values: FormValues): boolean => {
    return (
      values.firstName !== originalValues.firstName ||
      values.lastName !== originalValues.lastName ||
      selectedFile !== null
    );
  };

  if (!user) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <>
      <section className="profile credential-page card py-5 border-0 mb-0">
        <Container fluid className="py-5">
          <Row className="justify-content-center">
            <Col md={6} sm={12}>
              <div className="text-center mb-4">
                <div className="position-relative d-inline-block">
                  <div
                    style={{
                      width: 150,
                      height: 150,
                      borderRadius: "50%",
                      backgroundImage: `url(${user.profileImage})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      backgroundColor: "#FF9999",
                      margin: "0 auto"
                    }}
                  />
                  <label
                    htmlFor="profile-upload"
                    className="position-absolute p-2"
                    style={{ cursor: "pointer", bottom: "-20px", right: "0px" }}
                  >
                    <img src={IconAdd} alt="add" />
                    <input
                      id="profile-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{ display: "none" }}
                    />
                  </label>
                </div>
              </div>

              <Formik
                initialValues={{
                  firstName: user.firstName,
                  lastName: user.lastName
                }}
                validationSchema={ProfileSchema}
                enableReinitialize
                onSubmit={handleUpdate}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  isSubmitting
                }) => (
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        First Name <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="firstName"
                        value={values.firstName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={!!(touched.firstName && errors.firstName)}
                        className="p-3"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.firstName}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>
                        Last Name <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="lastName"
                        value={values.lastName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={!!(touched.lastName && errors.lastName)}
                        className="p-3"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.lastName}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        value={user.email}
                        disabled
                        readOnly
                        className="p-3 bg-default"
                      />
                      <Form.Text className="text-muted">
                        Email cannot be changed
                      </Form.Text>
                    </Form.Group>

                    <div className="d-grid">
                      <Button
                        variant="primary"
                        className="mt-2 border-0 text-white font-16 w-100 py-3 gilroy-semibold"
                        type="submit"
                        size="lg"
                        disabled={!hasChanges(values) || isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Updating...
                          </>
                        ) : (
                          "Update Profile"
                        )}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Toast Container - Make sure this is rendered */}
      <ToastContainer />
    </>
  );
};

export default Profile;